import React, { useState, useEffect, useRef } from "react";
import Rating from "./Rating";
import ProductReviewForm from "./ProductReviewForm";
import Review from "./Review";
import Loader from "./Loader";
import Pagination from "./Pagination";
import { useAuth } from "../services/hooks/useAuth";
import { usePostReview } from "../services/hooks/usePostReview";
import { useSnackbar } from "../services/hooks/useSnackbar";
import { useReviews } from "../services/hooks/useReviews";
import { useCurrentPage } from "../services/hooks/useCurrentPage";
import { useRequireLogin } from "../services/hooks/useRequireLogin";
import Notice from "./Notice";

const ProductReviews = React.memo(({ product }) => {
  const [sortOrder, setSortOrder] = useState("Most Recent");

  const { user } = useAuth();

  const { isPostingReview, postReview, postedReview, postReviewError } =
    usePostReview();

  const { addSnackbar } = useSnackbar();

  const {
    fetchReviews,
    reviews,
    reviewsError,
    reviewsStatus,
    reviewsNumPages,
  } = useReviews(product?.id);

  const { currentPage } = useCurrentPage();

  const { requireLogin } = useRequireLogin();

  const reviewFormCollapseRef = useRef();
  const reviewFormRef = useRef();

  const handleSortOrder = (e) => {
    e.preventDefault();

    setSortOrder(e.target.dataset?.sortOrder);
  };

  const onReview = (data) => {
    postReview({ ...data, productId: product.id });
  };

  useEffect(() => {
    if (currentPage) {
      fetchReviews(currentPage, { sortOrder });
    }
  }, [currentPage, sortOrder]);

  useEffect(() => {
    if (postReviewError) {
      addSnackbar(postReviewError, {
        type: "danger",
      });
    }
  }, [postReviewError]);

  useEffect(() => {
    if (postedReview) {
      addSnackbar(
        "Thanks for your feedback. It has been submitted for approval to ensure it meets our Review Guidelines.",
        {
          type: "success",
          dismissAfter: 6000,
        }
      );

      //Close and reset review form
      if (reviewFormRef?.current) {
        reviewFormRef.current.reset();
      }

      if (reviewFormCollapseRef?.current) {
        reviewFormCollapseRef.current.classList.remove("show");
      }
    }
  }, [postedReview]);

  if (reviewsError) {
    return (
      <section className="pt-9 pb-11" id="reviews">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <Notice
                message="The reviews for this product could not be displayed at this time or are turned off. Please contact support if you think you're seeing this message in error."
                type="danger"
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* REVIEWS */}
      <section className="pt-9 pb-11" id="reviews">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* Heading */}
              <h4 className="mb-10 text-center">Customer Reviews</h4>
              {/* Header */}
              <div className="row align-items-center">
                <div className="col-12 col-md-auto">
                  {/* Dropdown */}
                  <div className="dropdown mb-4 mb-md-0">
                    {/* Toggle */}
                    <a
                      className="dropdown-toggle text-reset"
                      data-toggle="dropdown"
                      href="#"
                    >
                      <strong>Sort by: {sortOrder}</strong>
                    </a>
                    {/* Menu */}
                    <div className="dropdown-menu mt-3">
                      <a
                        className="dropdown-item"
                        href="#"
                        data-sort-order="Most Recent"
                        onClick={handleSortOrder}
                      >
                        Most Recent
                      </a>
                      <a
                        className="dropdown-item"
                        href="#"
                        data-sort-order="Highest Rating"
                        onClick={handleSortOrder}
                      >
                        Highest Rating
                      </a>
                      <a
                        className="dropdown-item"
                        href="#"
                        data-sort-order="Lowest Rating"
                        onClick={handleSortOrder}
                      >
                        Lowest Rating
                      </a>
                      <a
                        className="dropdown-item"
                        href="#"
                        data-sort-order="Most Helpful"
                        onClick={handleSortOrder}
                      >
                        Most Helpful
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md text-md-center">
                  {/* Rating */}
                  <Rating
                    rating={product.rating}
                    size="h6"
                    className={"mb-4 mb-md-0 h6"}
                  />
                  {/* Count */}
                  <strong className="font-size-sm ml-2">
                    Reviews ({product.reviews.length})
                  </strong>
                </div>
                <div className="col-12 col-md-auto">
                  {/* Button */}
                  {user ? (
                    <a
                      className="btn btn-sm btn-dark"
                      data-toggle="collapse"
                      href="#reviewForm"
                    >
                      Write Review
                    </a>
                  ) : (
                    <a
                      href="#"
                      className="btn btn-sm btn-dark"
                      onClick={requireLogin}
                    >
                      Write Review
                    </a>
                  )}
                </div>
              </div>
              {/* New Review */}
              <div
                ref={reviewFormCollapseRef}
                className="collapse"
                id="reviewForm"
              >
                {/* Divider */}
                <hr className="my-8" />

                <ProductReviewForm
                  ref={reviewFormRef}
                  onReview={onReview}
                  isPostingReview={isPostingReview}
                />
              </div>

              {reviewsStatus !== "ready" ? (
                <Loader />
              ) : reviews && reviews.length ? (
                <>
                  {/* Reviews */}
                  <div className="mt-8">
                    <p className="text-center">
                      Behind every review is an experience that matters. Leave a
                      review for this product.
                    </p>

                    {reviews.map((review) => (
                      <Review key={review.id} {...review} />
                    ))}
                  </div>
                  {/* Pagination */}

                  <Pagination numPages={reviewsNumPages} anchor="reviews" />
                </>
              ) : (
                <div className="mt-8">
                  <p className="text-center">
                    Behind every review is an experience that matters. Be the
                    first to review this product.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
});

export default ProductReviews;
